import { useEffect, useState } from "react";
import * as React from "react";

import dayjs from "dayjs";
import {
  Button,
  FormHelperText,
  Grid,
  InputLabel,
  Stack,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Divider,
  DialogActions,
  Box,
  Autocomplete,
  Typography,
  useTheme,
  Switch,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";

import * as Yup from "yup";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { openSnackbar } from "../../../../../api/snackbar";
import { USERAXIOS } from "../../../../../api";
import { SnackbarProps } from "../../../../../types/snackbar";
import TextArea from "antd/es/input/TextArea";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormik, FormikProvider, Form } from "formik";

import { Country, City } from "../../../../../types/site-params/Country";
import { ResumeCourse } from "../../../../../types/resumes-managment/resume";
import ImageUploader from "../../../../../components/ImageUploader"; // Assuming this component exists
import { useCountriesForAutocomplete } from "../../../../../hooks/query-hooks/useCountries";
import { useCitiesForAutocomplete } from "../../../../../hooks/query-hooks/useCities";
import { useDebounce } from "../../../../../hooks";

const getInitialValues = (experience: ResumeCourse | null) => {
  if (experience && experience.id) {
    return {
      institute: experience.institute || "",
      course_name: experience.course_name || "",
      country_id: experience.country_id || null,
      city_id: experience.city_id || null,
      certificate_date: experience.certificate_date || "",
      course_field: experience.course_field || "",
      course_descriptions: experience.course_descriptions || "",
      certificate: experience.certificate || false,
      certificate_photo: experience.certificate_photo || "",
      additional_info: experience.additional_info || "",
    };
  } else {
    return {
      institute: "",
      course_name: "",
      country_id: null,
      city_id: null,
      certificate_date: "",
      course_field: "",
      course_descriptions: "",
      certificate: false,
      certificate_photo: "",
      additional_info: "",
    };
  }
};

const CourseSchema = Yup.object().shape({
  institute: Yup.string().required("Institute is required"),
  course_name: Yup.string().required("Course Name is required"),
  country_id: Yup.number().required("Country is required"),
  city_id: Yup.number().required("City is required"),
  certificate_date: Yup.string().required("Certificate Date is required"),
  course_field: Yup.string().required("Course Field is required"),
  course_descriptions: Yup.string().required("Course Descriptions is required"),
  certificate: Yup.boolean(),
  certificate_photo: Yup.mixed().required("Certificate Photo is required"),
  additional_info: Yup.string(),
});

function AddEditResumeCourseForm({
  setResult,
  resumeId,
  open,
  setOpen,
  item,
}: {
  setResult: any;
  resumeId: any;
  open: boolean;
  setOpen: (openLanguage: boolean) => void;
  item: ResumeCourse | null;
}) {
  const [serverErrors, setServerErrors] = useState<Record<string, string[]>>(
    {}
  );

  // Search states for autocomplete
  const [countrySearchTerm, setCountrySearchTerm] = useState("");
  const [citySearchTerm, setCitySearchTerm] = useState("");

  // Debounced search terms
  const debouncedCountrySearchTerm = useDebounce(countrySearchTerm, 500);
  const debouncedCitySearchTerm = useDebounce(citySearchTerm, 500);

  const theme = useTheme();
  const intl = useIntl();

  const handleClose = () => {
    setOpen(false);
  };

  const formik = useFormik({
    initialValues: getInitialValues(item ?? null),
    validationSchema: CourseSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const formData = new FormData();

        formData.append("resume_id", resumeId.toString());
        formData.append("institute", values.institute);
        formData.append("course_name", values.course_name);
        formData.append("country_id", values.country_id?.toString() || "");
        formData.append("city_id", values.city_id?.toString() || "");
        formData.append(
          "certificate_date",
          values.certificate_date?.toString() || ""
        );
        formData.append("course_field", values.course_field);
        formData.append("course_descriptions", values.course_descriptions);
        formData.append("certificate", values.certificate ? "1" : "0");
        formData.append("additional_info", values.additional_info);

        if (
          values?.certificate_photo &&
          typeof values.certificate_photo !== "string"
        ) {
          formData.append("certificate_photo", values.certificate_photo);
        }

        if (item && item?.id) {
          const response = await USERAXIOS.post(
            `resume/courses/${item?.id}`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );
          const updatedExperience = response.data.data;

          if (updatedExperience) {
            setResult((prev: ResumeCourse[]) =>
              prev.map((item) =>
                item.id === updatedExperience.id ? updatedExperience : item
              )
            );

            openSnackbar({
              open: true,
              message: "Course updated successfully",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        } else {
          const response = await USERAXIOS.post("resume/courses", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
          const newExperience = response.data.data;

          if (newExperience) {
            setResult((prev: ResumeCourse[]) => [...prev, newExperience]);

            openSnackbar({
              open: true,
              message: "Course Added successfully",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        }

        setSubmitting(false);
        handleClose();
      } catch (error) {
        setServerErrors(error.response.data.message.message);
        console.error(
          "Error submitting form:",
          error.response.data.message.message
        );
        openSnackbar({
          open: true,
          message: "Something went wrong",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: { color: "error" },
        } as SnackbarProps);
        setSubmitting(false);
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    values,
    handleChange,
  } = formik;

  const {
    items: countries,
    loading: loadingCountries,
    hasMore: hasMoreCountries,
    loadMore: loadMoreCountries,
    error: countriesError,
  } = useCountriesForAutocomplete(20, debouncedCountrySearchTerm);

  const {
    items: cities,
    loading: loadingCities,
    hasMore: hasMoreCities,
    loadMore: loadMoreCities,
    error: citiesError,
  } = useCitiesForAutocomplete(values?.country_id, 20, debouncedCitySearchTerm);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        color="primary"
        style={{
          textAlign: "center",
          fontSize: "22px",
          fontWeight: "bold",
          marginBottom: "10px",
        }}
      >
        {item && item?.id ? (
          <FormattedMessage id="Edit Course" />
        ) : (
          <FormattedMessage id="Add Course" />
        )}
      </DialogTitle>
      <Divider />

      <DialogContent
        sx={{ height: "100%", display: "flex", flexDirection: "column", p: 2 }}
      >
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="institute">
                    <FormattedMessage id="institute" />
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="institute"
                    name="institute"
                    value={values.institute}
                    onChange={handleChange}
                    placeholder="Institute"
                    error={touched.institute && Boolean(errors.institute)}
                  />
                  {touched.institute && errors.institute && (
                    <FormHelperText error>{errors.institute}</FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="course-name">
                    <FormattedMessage id="Course name" />
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="course-name"
                    name="course_name"
                    value={values.course_name}
                    onChange={handleChange}
                    placeholder="Course Name"
                    error={touched.course_name && Boolean(errors.course_name)}
                  />
                  {touched.course_name && errors.course_name && (
                    <FormHelperText error>{errors.course_name}</FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="course-field">
                    <FormattedMessage id="Course field" />
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="course-field"
                    name="course_field"
                    value={values.course_field}
                    onChange={handleChange}
                    placeholder="Course Field"
                    error={touched.course_field && Boolean(errors.course_field)}
                  />
                  {touched.course_field && errors.course_field && (
                    <FormHelperText error>{errors.course_field}</FormHelperText>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="certificate-year">
                    <FormattedMessage id="Certificate year" />
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="certificate-year"
                    name="certificate_date"
                    value={values.certificate_date}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/[^0-9]/g, "")
                        .slice(0, 4);
                      setFieldValue("certificate_date", value);
                    }}
                    placeholder="Year (e.g. 2024)"
                    inputProps={{ maxLength: 4 }}
                    error={Boolean(
                      (touched.certificate_date && errors.certificate_date) ||
                        (serverErrors && serverErrors.certificate_date)
                    )}
                  />
                  {touched.certificate_date && errors.certificate_date && (
                    <FormHelperText error>
                      {errors.certificate_date}
                    </FormHelperText>
                  )}
                  {serverErrors && serverErrors.certificate_date && (
                    <FormHelperText error>
                      {serverErrors.certificate_date.join(" ")}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="Country" />
                  </Typography>
                  <Autocomplete
                    id="country-select"
                    options={countries || []}
                    getOptionLabel={(option: Country) =>
                      theme.direction === "rtl"
                        ? option.name_trans.ar
                        : option.name
                    }
                    value={
                      countries?.find(
                        (country) => country.id === values.country_id
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      const selectedCountryId = newValue?.id || null;

                      setFieldValue("country_id", selectedCountryId);
                      setFieldValue("city_id", null);
                      // Clear search term when option is selected
                      if (newValue) {
                        setCountrySearchTerm("");
                      }
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "input") {
                        setCountrySearchTerm(newInputValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select country"
                        error={touched.country_id && Boolean(errors.country_id)}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingCountries && (
                                <CircularProgress color="inherit" size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    ListboxProps={{
                      onScroll: (event) => {
                        const listboxNode = event.currentTarget;
                        const threshold = 100;
                        if (
                          listboxNode.scrollHeight - listboxNode.scrollTop <=
                            listboxNode.clientHeight + threshold &&
                          hasMoreCountries &&
                          !loadingCountries
                        ) {
                          loadMoreCountries();
                        }
                      },
                    }}
                    loading={loadingCountries}
                    filterOptions={(x) => x} // Disable built-in filtering
                    noOptionsText={
                      debouncedCountrySearchTerm.length >= 2
                        ? "No countries found"
                        : "Type to search countries"
                    }
                  />
                  {touched.country_id && errors.country_id && (
                    <FormHelperText error>{errors.country_id}</FormHelperText>
                  )}
                  {countriesError && (
                    <FormHelperText error>
                      Error loading countries. Please try again.
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="City" />
                  </Typography>
                  <Autocomplete
                    id="city-select"
                    options={cities}
                    getOptionLabel={(option: City) =>
                      theme.direction === "rtl"
                        ? option.name_trans.ar
                        : option.name
                    }
                    value={
                      cities.find((city) => city.id === values.city_id) || null
                    }
                    onChange={(_, newValue) => {
                      setFieldValue("city_id", newValue?.id || null);
                      // Clear search term when option is selected
                      if (newValue) {
                        setCitySearchTerm("");
                      }
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "input") {
                        setCitySearchTerm(newInputValue);
                      }
                    }}
                    disabled={!values.country_id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select city"
                        error={touched.city_id && Boolean(errors.city_id)}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingCities && (
                                <CircularProgress color="inherit" size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    ListboxProps={{
                      onScroll: (event) => {
                        const listboxNode = event.currentTarget;
                        const threshold = 100;
                        if (
                          listboxNode.scrollHeight - listboxNode.scrollTop <=
                            listboxNode.clientHeight + threshold &&
                          hasMoreCities &&
                          !loadingCities
                        ) {
                          loadMoreCities();
                        }
                      },
                    }}
                    loading={loadingCities}
                    filterOptions={(x) => x} // Disable built-in filtering
                    noOptionsText={
                      debouncedCitySearchTerm.length >= 2
                        ? "No cities found"
                        : "Type to search cities"
                    }
                  />
                  {touched.city_id && errors.city_id && (
                    <FormHelperText error>{errors.city_id}</FormHelperText>
                  )}
                  {citiesError && (
                    <FormHelperText error>
                      Error loading cities. Please try again.
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>
              {/* <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="Country" />
                  </Typography>
                  <Autocomplete
                    id="country-select"
                    options={countries || []}
                    getOptionLabel={(option: Country) =>
                      theme.direction === "rtl"
                        ? option.name_trans.ar
                        : option.name
                    }
                    value={
                      countries?.find(
                        (country) => country.id === values.country_id
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      const selectedCountryId = newValue?.id || null;
                      setFieldValue("country_id", selectedCountryId);
                      setFieldValue("city_id", null);
                    }}
                    loading={countriesLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select country"
                        error={
                          (touched.country_id && Boolean(errors.country_id)) ||
                          Boolean(countriesError)
                        }
                      />
                    )}
                  />
                  {touched.country_id && errors.country_id && (
                    <FormHelperText error>{errors.country_id}</FormHelperText>
                  )}
                  {countriesError && (
                    <FormHelperText error>
                      Error loading countries. Please try again.
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="City" />
                  </Typography>
                  <Autocomplete
                    id="city-select"
                    options={cities}
                    getOptionLabel={(option: City) =>
                      theme.direction === "rtl"
                        ? option.name_trans.ar
                        : option.name
                    }
                    value={
                      cities.find((city) => city.id === values.city_id) || null
                    }
                    onChange={(_, newValue) => {
                      setFieldValue("city_id", newValue?.id || null);
                    }}
                    disabled={!values.country_id}
                    loading={citiesLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select city"
                        error={
                          (touched.city_id && Boolean(errors.city_id)) ||
                          Boolean(citiesError)
                        }
                      />
                    )}
                  />
                  {touched.city_id && errors.city_id && (
                    <FormHelperText error>{errors.city_id}</FormHelperText>
                  )}
                  {citiesError && (
                    <FormHelperText error>
                      Error loading cities. Please try again.
                    </FormHelperText>
                  )}
                </Stack>
              </Grid> */}

              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="course-descriptions">
                    <FormattedMessage id="course-descriptions" />
                  </InputLabel>
                  <TextArea
                    rows={4}
                    id="course-descriptions"
                    value={values.course_descriptions}
                    name="course_descriptions"
                    onChange={handleChange}
                    placeholder={intl.formatMessage({
                      id: "course-descriptions",
                      defaultMessage: "Course Descriptions",
                    })}
                  />
                  {touched.course_descriptions &&
                    errors.course_descriptions && (
                      <FormHelperText error>
                        {errors.course_descriptions}
                      </FormHelperText>
                    )}
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="additional-info">
                    <FormattedMessage id="additional-info" />
                  </InputLabel>
                  <TextArea
                    rows={4}
                    id="additional-info"
                    value={values.additional_info}
                    name="additional_info"
                    onChange={handleChange}
                    placeholder={intl.formatMessage({
                      id: "additional-info",
                      defaultMessage: "Additional Info",
                    })}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Stack>
                  <Typography>
                    <FormattedMessage id="Certificate Photo" />
                  </Typography>
                  <ImageUploader
                    id="certificate-photo"
                    previewUrl={
                      typeof values.certificate_photo === "string"
                        ? values.certificate_photo
                        : values.certificate_photo
                        ? URL.createObjectURL(values.certificate_photo)
                        : null
                    }
                    onChange={(e, setFieldValue) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setFieldValue("certificate_photo", file);
                      }
                    }}
                    setFieldValue={setFieldValue}
                    label="Click to upload certificate photo"
                  />
                  {touched.certificate_photo && errors.certificate_photo && (
                    <FormHelperText error>
                      {errors.certificate_photo}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="Certificate" />
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.certificate}
                        onChange={(event) => {
                          setFieldValue("certificate", event.target.checked);
                        }}
                        name="certificate"
                      />
                    }
                    label={
                      values.certificate ? "Has Certificate" : "No Certificate"
                    }
                  />
                  {touched.certificate && errors.certificate && (
                    <FormHelperText error>{errors.certificate}</FormHelperText>
                  )}
                  {serverErrors && serverErrors.certificate && (
                    <FormHelperText error>
                      {serverErrors.certificate.join(" ")}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>
            </Grid>

            <DialogActions sx={{ p: 2.5 }}>
              <Grid container justifyContent="center" alignItems="center">
                <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                  <Grid item md={2} />
                  <Grid item md={4}>
                    <Button
                      disabled={isSubmitting}
                      fullWidth
                      type="submit"
                      variant="contained"
                    >
                      <FormattedMessage
                        id={item && item?.id ? "Update" : "Submit"}
                      />
                    </Button>
                  </Grid>
                  <Grid item md={4}>
                    <Button
                      fullWidth
                      color="secondary"
                      variant="outlined"
                      onClick={handleClose}
                    >
                      <FormattedMessage id="Cancel" />
                    </Button>
                  </Grid>
                  <Grid item md={2} />
                </Stack>
              </Grid>
            </DialogActions>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}

export default AddEditResumeCourseForm;
